import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Avatar,
  Typography,
  TextField,
  IconButton,
  Paper,
  Stack,
  Divider,
  useMediaQuery,
  Drawer,
  Menu,
  MenuItem,
  Chip,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  Send,
  MoreVert,
  AddCircle,
  Menu as MenuIcon,
  ArrowBack,
  PictureAsPdf,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { useLocation, useNavigate } from "react-router-dom";
import { getCurrentUser } from "../services/auth.js";
import {
  createMessage,
  getMessages,
  markChatRead,
  releaseChatLock,
} from "../services/source.js";

// =========================
// SIDEBAR COMPONENT (FIXED)
// =========================
const SidebarContent = ({
  topics,
  activeTopicId,
  setActiveTopicId,
  newSubject,
  setNewSubject,
  createTopic,
  setOpenSidebar,
}) => {
  return (
    <Box
      sx={{
        width: 350,
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      {/* HEADER */}
      <Box
        sx={{
          p: 3.5,
          background: "#898c8f",
          borderRight: "1px solid #dddddd",
          color: "#fff",
        }}
      >
        <Typography fontWeight="bold" variant="h5" sx={{ color: "#424242;" }}>
          Topicos de conversa 💬
        </Typography>
      </Box>

      {/* CREATE TOPIC */}
      <Box sx={{ p: 2, display: "flex", gap: 1 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Novo assunto..."
          value={newSubject}
          onChange={(e) => setNewSubject(e.target.value)}
        />

        <IconButton
          onClick={createTopic}
          sx={{
            "&:hover": {
              backgroundColor: "transparent",
            },
          }}
        >
          <AddCircle
            sx={{
              color: "#943dbf",
              fontSize: "150%",
              mt: -1,
            }}
          />
        </IconButton>
      </Box>

      <Divider />

      {/* TOPICS LIST */}
      <Box sx={{ overflowY: "auto", flex: 1 }}>
        {topics.map((topic) => (
          <Box
            key={topic.id}
            onClick={() => {
              setActiveTopicId(topic.id);
              setOpenSidebar(false);
            }}
            sx={{
              p: 2,
              cursor: "pointer",
              background:
                topic.id === activeTopicId ? "#f0f0f0" : "transparent",
              "&:hover": { background: "#f5f5f5" },
            }}
          >
            <Typography fontWeight="bold">{topic.subject}</Typography>

            <Typography variant="body2" color="text.secondary">
              {topic.messages?.[topic.messages.length - 1]?.text}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

// =========================
// MAIN CHAT COMPONENT
// =========================
const ChatScreen = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const location = useLocation();
  const navigate = useNavigate();
  const user = getCurrentUser();
  const type = user?.data?.type || "";
  const sessionToken = user?.data?.sessionToken || "";
  const userId = user?.data?.id || "";
  const userType = type || "";
  const isAccessClientLogin = Boolean(user?.data?.clientRootId);

  const client = location.state?.client || user.data;
  const resolvedClientId =
    (isAccessClientLogin
      ? user?.data?.clientRootId
      : location.state?.client?.id) ||
    client?.id ||
    user?.data?.id ||
    null;
  const resolvedCompanyId =
    (isAccessClientLogin
      ? user?.data?.companyId
      : location.state?.client?.companyId) ||
    client?.companyId ||
    user?.data?.companyId ||
    null;
  // MOBILE SIDEBAR STATE
  const [openSidebar, setOpenSidebar] = useState(false);
  // DATA
  const [topics, setTopics] = useState([]);
  const [activeTopicId, setActiveTopicId] = useState(1);
  const [message, setMessage] = useState("");
  const [newSubject, setNewSubject] = useState("");
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [chatLocked, setChatLocked] = useState(false);
  const [lockMessage, setLockMessage] = useState("");
  const [lockAlertOpen, setLockAlertOpen] = useState(false);

  useEffect(() => {
    const releaseLock = () => {
      if (!resolvedClientId || !resolvedCompanyId || !sessionToken) {
        return;
      }

      releaseChatLock({
        company_id: resolvedCompanyId,
        client_id: resolvedClientId,
        session_token: sessionToken,
      });
    };

    const handleBeforeUnload = () => {
      if (!resolvedClientId || !resolvedCompanyId || !sessionToken) {
        return;
      }

      const apiUrl = process.env.REACT_APP_API_URL;
      if (!apiUrl || !navigator?.sendBeacon) {
        return;
      }

      const payload = JSON.stringify({
        company_id: resolvedCompanyId,
        client_id: resolvedClientId,
        session_token: sessionToken,
      });

      const blob = new Blob([payload], { type: "application/json" });
      navigator.sendBeacon(`${apiUrl}/releaseChatLock.php`, blob);
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      releaseLock();
    };
  }, [resolvedClientId, resolvedCompanyId, sessionToken]);

  const activeTopic = topics.find((t) => t.id === activeTopicId);

  const getMessageText = useCallback((msg) => {
    const textValue =
      msg.text ??
      msg.message ??
      msg.mensagem ??
      msg.texto ??
      msg.mensage ??
      msg.message_text ??
      msg.chat_message ??
      msg.content ??
      msg.body ??
      "";

    return typeof textValue === "string" ? textValue : String(textValue);
  }, []);

  const normalizeMessage = useCallback(
    (msg) => {
      const text = getMessageText(msg);

      return {
        id: msg.id ?? msg.message_id ?? Date.now(),
        text,
        sender: msg.sender || msg.from || "company",
        time:
          msg.time ||
          (msg.created_at
            ? new Date(msg.created_at).toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })
            : new Date().toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })),
        pdf: msg.file_url
          ? {
              name: msg.file_name || "Anexo",
              url: msg.file_url,
            }
          : null,
      };
    },
    [getMessageText],
  );

  const buildTopicsFromMessages = useCallback(
    (messages) => {
      if (!messages || messages.length === 0) {
        return [];
      }

      const topicMap = {};

      messages.forEach((rawMessage) => {
        const topicId =
          Number(rawMessage.topic_id ?? rawMessage.topicId ?? 1) || 1;
        if (!topicMap[topicId]) {
          topicMap[topicId] = {
            id: topicId,
            subject: topicId === 1 ? "Geral" : `Assunto ${topicId}`,
            messages: [],
          };
        }

        topicMap[topicId].messages.push(normalizeMessage(rawMessage));
      });

      const topicsList = Object.values(topicMap).map((topic) => {
        const topicLabel = topic.messages.find(
          (msg) =>
            msg.sender === "system" &&
            typeof msg.text === "string" &&
            msg.text.trim().toLowerCase().startsWith("tópico:"),
        );

        if (topicLabel) {
          topic.subject =
            topicLabel.text.replace(/^Tópico:\s*/i, "").trim() || topic.subject;
        }

        return topic;
      });

      return topicsList.sort((a, b) => b.id - a.id);
    },
    [normalizeMessage],
  );

  // LOAD MESSAGES
  const loadMessages = useCallback(async () => {
    try {
      if (!resolvedClientId || !resolvedCompanyId || !sessionToken) return;

      const response = await getMessages(resolvedCompanyId, resolvedClientId, {
        session_token: sessionToken,
        user_id: userId,
        user_type: userType,
      });
      const builtTopics = buildTopicsFromMessages(response.messages || []);

      setChatLocked(false);
      setLockMessage("");
      setTopics(builtTopics);

      setActiveTopicId((prevActiveTopicId) => {
        if (!builtTopics.some((topic) => topic.id === prevActiveTopicId)) {
          return builtTopics[0]?.id || 1;
        }
        return prevActiveTopicId;
      });

      await markChatRead({
        company_id: resolvedCompanyId,
        client_id: resolvedClientId,
        user_id: userId,
        user_type: userType,
      });
    } catch (error) {
      if (error?.response?.status === 409) {
        setChatLocked(true);
        setTopics([]);
        setLockMessage(
          error?.response?.data?.message ||
            "Este chat já está aberto por outro utilizador.",
        );
        setLockAlertOpen(true);
        return;
      }

      console.error(error);
    }
  }, [
    buildTopicsFromMessages,
    resolvedClientId,
    resolvedCompanyId,
    sessionToken,
    userId,
    userType,
  ]);

  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  useEffect(() => {
    if (!resolvedClientId || !resolvedCompanyId || !sessionToken) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      loadMessages();
    }, 3000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [loadMessages, resolvedClientId, resolvedCompanyId, sessionToken]);

  // CREATE TOPIC (FIXED)
  const createTopic = async () => {
    if (chatLocked) return;
    if (!newSubject.trim()) return;

    const topicId = Date.now();
    const systemMessage = {
      id: topicId,
      text: `Tópico: ${newSubject}`,
      sender: "system",
      time: new Date().toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      }),
    };

    const newTopic = {
      id: topicId,
      subject: newSubject,
      messages: [systemMessage],
    };

    setTopics((prev) => [newTopic, ...prev]);
    setActiveTopicId(topicId);
    setNewSubject("");

    try {
      const payload = {
        user_id: resolvedClientId,
        company_id: resolvedCompanyId,
        topic_id: topicId,
        sender: "system",
        text: systemMessage.text,
        file_url: null,
        file_name: null,
        file_type: null,
      };

      await createMessage(payload);
    } catch (error) {
      console.error("Error creating topic system message:", error);
    }
  };
  // SEND MESSAGE
  const sendMessage = async () => {
    if (chatLocked) return;
    if (!message.trim()) return;

    try {
      const payload = {
        user_id: resolvedClientId,
        company_id: resolvedCompanyId,
        topic_id: activeTopicId,
        sender: type === "client" ? "client" : "company",
        text: message,
        file_url: null,
        file_name: null,
        file_type: null,
      };

      const savedMessage = await createMessage(payload);

      if (!savedMessage) return;

      const normalizedSavedMessage = normalizeMessage(savedMessage);

      setTopics((prev) =>
        prev.map((topic) =>
          topic.id === activeTopicId
            ? {
                ...topic,
                messages: [...topic.messages, normalizedSavedMessage],
              }
            : topic,
        ),
      );

      setMessage("");
    } catch (error) {
      console.error(error);
    }
  };

  const openMenu = (event) => {
    setMenuAnchor(event.currentTarget);
  };

  const closeMenu = () => {
    setMenuAnchor(null);
  };

  const goBack = () => {
    closeMenu();
    navigate(-1); // go to previous page
  };

  return (
    <Box sx={{ height: "100vh", display: "flex", background: "#ece5dd" }}>
      {/* DESKTOP SIDEBAR */}
      {!isMobile && (
        <Box sx={{ width: 350, borderRight: "1px solid #ddd" }}>
          <SidebarContent
            topics={topics}
            activeTopicId={activeTopicId}
            setActiveTopicId={setActiveTopicId}
            newSubject={newSubject}
            setNewSubject={setNewSubject}
            createTopic={createTopic}
            setOpenSidebar={setOpenSidebar}
          />
        </Box>
      )}

      {/* MOBILE DRAWER */}
      <Drawer
        anchor="left"
        open={openSidebar}
        onClose={() => setOpenSidebar(false)}
      >
        <SidebarContent
          topics={topics}
          activeTopicId={activeTopicId}
          setActiveTopicId={setActiveTopicId}
          newSubject={newSubject}
          setNewSubject={setNewSubject}
          createTopic={createTopic}
          setOpenSidebar={setOpenSidebar}
        />
      </Drawer>

      {/* CHAT AREA */}
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
        {/* HEADER */}
        <Box
          sx={{
            p: 2,
            background: "#898c8f",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            {/* MOBILE MENU BUTTON */}
            {isMobile && (
              <IconButton
                sx={{ color: "#fff" }}
                onClick={() => setOpenSidebar(true)}
              >
                <MenuIcon />
              </IconButton>
            )}

            <Avatar sx={{ bgcolor: "#9c27b0", width: 56, height: 56 }}>
              {client?.nome?.charAt(0) || "C"}
            </Avatar>
            <Box>
              <Typography fontWeight="bold">
                {client?.nome || "Cliente"}
              </Typography>

              <Typography variant="body2">
                {client?.empresa || "Empresa"}
              </Typography>
            </Box>
          </Stack>

          <>
            <IconButton sx={{ color: "#fff" }} onClick={openMenu}>
              <MoreVert />
            </IconButton>

            <Menu
              anchorEl={menuAnchor}
              open={Boolean(menuAnchor)}
              onClose={closeMenu}
            >
              <MenuItem onClick={goBack}>
                <Chip
                  label="Voltar"
                  color="default"
                  icon={<ArrowBack color="inherit" />}
                  sx={{
                    cursor: "pointer",
                    backgroundColor: "rgb(165, 52, 222)",
                    color: "white",
                    fontWeight: 600,
                    height: 40,
                  }}
                />
              </MenuItem>
            </Menu>
          </>

          {/* <IconButton sx={{ color: "#fff" }}>
            <MoreVert />
          </IconButton> */}
        </Box>

        {/* MESSAGES */}
        <Box sx={{ flex: 1, overflowY: "auto", p: 2 }}>
          <Stack spacing={2}>
            {activeTopic?.messages?.map((msg) => (
              <Box
                key={msg.id}
                sx={{
                  display: "flex",
                  justifyContent:
                    msg.sender === type || msg.sender === "me"
                      ? "flex-end"
                      : "flex-start",
                }}
              >
                <Paper
                  sx={{
                    p: 1.5,
                    maxWidth: "75%",
                    background:
                      msg.sender === type || msg.sender === "me"
                        ? "#d9fdd3"
                        : msg.sender === "system"
                          ? "#f1f1f1"
                          : "#fff",
                    borderRadius: 3,
                  }}
                >
                  <>
                    {((msg.text ||
                      msg.message ||
                      msg.mensagem ||
                      msg.texto ||
                      msg.mensage ||
                      msg.message_text ||
                      msg.chat_message ||
                      msg.content ||
                      msg.body) && (
                      <Typography>
                        {msg.text ||
                          msg.message ||
                          msg.mensagem ||
                          msg.texto ||
                          msg.mensage ||
                          msg.message_text ||
                          msg.chat_message ||
                          msg.content ||
                          msg.body}
                      </Typography>
                    )) || (
                      <Typography color="text.secondary">Sem texto</Typography>
                    )}

                    {msg.pdf && (
                      <Paper
                        variant="outlined"
                        sx={{
                          mt:
                            msg.text ||
                            msg.message ||
                            msg.mensagem ||
                            msg.texto ||
                            msg.mensage ||
                            msg.message_text ||
                            msg.chat_message
                              ? 1
                              : 0,
                          p: 1,
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <PictureAsPdf color="error" />

                        <a
                          href={msg.pdf.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            textDecoration: "none",
                            color: "#1976d2",
                            fontWeight: 500,
                          }}
                        >
                          {msg.pdf.name}
                        </a>
                      </Paper>
                    )}
                  </>

                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      textAlign: "right",
                      mt: 0.5,
                      color: "gray",
                    }}
                  >
                    {msg.time}
                  </Typography>
                </Paper>
              </Box>
            ))}
          </Stack>
        </Box>

        {/* INPUT */}
        <Box
          sx={{
            p: 2,
            background: "#f0f2f5",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <TextField
            fullWidth
            size="small"
            placeholder="Digite uma mensagem"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            disabled={chatLocked}
            onKeyDown={(e) => {
              if (e.key === "Enter") sendMessage();
            }}
            sx={{ background: "#fff", borderRadius: 10 }}
          />

          <IconButton
            onClick={sendMessage}
            disabled={chatLocked}
            sx={{
              background: "#25d366",
              color: "#fff",
              "&:hover": { background: "#20ba5a" },
            }}
          >
            <Send />
          </IconButton>
        </Box>
      </Box>
      <Snackbar
        open={lockAlertOpen}
        autoHideDuration={5000}
        onClose={() => setLockAlertOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="warning"
          onClose={() => setLockAlertOpen(false)}
          sx={{ width: "100%" }}
        >
          {lockMessage || "Este chat já está aberto por outro utilizador."}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ChatScreen;
